import React, { FC } from 'react';

import { useTranslation } from '@modernloop/i18n';
import type { DownloadReason } from '@modernloop/shared/components';
import { Loader } from '@modernloop/shared/helper-components';
import { Stack, Typography } from '@mui/material';

import { DataDownloadType, DeclineMetricesQuery } from 'src/generated/mloop-graphql';

import { DataPoint, DataPoints } from 'src/components/Charts';
import PieChart, { DataKey } from 'src/components/Charts/Pie';

import { ElementType } from 'src/types/utils';

import SectionWrap from 'src/views-new/MetricsPage/SectionWrap';
import { useExportCsvWithDownloadReason } from 'src/utils/download/exportCsvWithDownloadReason';

export enum DeclineReasons {
  conflict = 'conflict',
  know_the_candidate = 'know_the_candidate',
  out_of_office = 'out_of_office',
  outside_work_hours = 'outside_work_hours',
  over_limit = 'over_limit',
  other = 'other',
  missed = 'missed',
}

const DECLINE_REASON_LABEL_KEYS: Record<DeclineReasons, string> = {
  [DeclineReasons.conflict]: 'hiring-dashboard-metrics:metrics.declineReason.conflict',
  [DeclineReasons.know_the_candidate]: 'hiring-dashboard-metrics:metrics.declineReason.knowTheCandidate',
  [DeclineReasons.out_of_office]: 'hiring-dashboard-metrics:metrics.declineReason.outOfOffice',
  [DeclineReasons.outside_work_hours]: 'hiring-dashboard-metrics:metrics.declineReason.outsideWorkHours',
  [DeclineReasons.over_limit]: 'hiring-dashboard-metrics:metrics.declineReason.overLimit',
  [DeclineReasons.other]: 'hiring-dashboard-metrics:metrics.declineReason.other',
  [DeclineReasons.missed]: 'hiring-dashboard-metrics:metrics.declineReason.missed',
};

/** Returns the localized display label for a decline reason key. */
export const getDeclineReasonLabel = (t: (key: string) => string, reasonKey: string): string => {
  const labelKey = DECLINE_REASON_LABEL_KEYS[reasonKey as DeclineReasons];
  if (labelKey) {
    return t(labelKey);
  }
  return reasonKey;
};

const DECLINE_REASON_KEYS: string[] = [
  'missed',
  'outside_work_hours',
  'out_of_office',
  'conflict',
  'over_limit',
  'know_the_candidate',
  'other',
];

interface Props {
  loading?: boolean;
  data: ElementType<
    ElementType<ElementType<DeclineMetricesQuery['interviewerMetricsDeclinesWrapper']>['declineReasonsChart']>['data']
  >;
}

const DeclineReasonsChart: FC<Props> = ({ loading, data }) => {
  const { t } = useTranslation();
  const exportCsv = useExportCsvWithDownloadReason();

  const declineReasonsDataKeys: DataKey[] = DECLINE_REASON_KEYS.map((key) => ({
    key,
    label: getDeclineReasonLabel(t, key),
  }));
  const series: DataPoints =
    (data.length &&
      data.map((val) => {
        return {
          x: val?.x,
          y: val?.y,
        } as DataPoint;
      })) ||
    [];

  const onDownload = async (reason: DownloadReason) => {
    const total = series.reduce((partialSum, val) => {
      return partialSum + (val.y ?? 0);
    }, 0);
    const formattedData = series.map((item) => {
      const x = item.x as string;
      const y = item.y ?? 0;
      return {
        Reason: getDeclineReasonLabel(t, x),
        Percentage: total ? ((y / total) * 100).toFixed(2) : 0,
        Value: y.toFixed(2),
      };
    });
    return exportCsv(DataDownloadType.MetricsInterviewerDeclineReasons, reason, {
      data: formattedData,
      fileName: 'Decline reasons',
      exportType: 'csv',
    });
  };

  const headerContent = (): JSX.Element | null => {
    if (!series.length) {
      return null;
    }

    const nonZeroValue = series.filter((a) => {
      return !!a.y;
    });

    const maxValDataPoint = nonZeroValue.reduce((a, b) => {
      return (a.y ?? 0) > (b.y ?? 0) ? a : b;
    });

    const maxValueDataKey = declineReasonsDataKeys.find((dataKey) => {
      return dataKey.key === maxValDataPoint?.x;
    });

    if (maxValDataPoint?.y) {
      return (
        <Stack direction="row" alignItems="baseline" spacing={1}>
          <Typography variant="h2" color="info">
            {maxValDataPoint.y}
          </Typography>
          <Typography variant="h5" color="info">
            {maxValueDataKey && maxValueDataKey.label}
            {nonZeroValue.length > 1 &&
              t('hiring-dashboard-metrics:metrics.moreReasonsWithSeparator', { count: nonZeroValue.length - 1 })}
          </Typography>
        </Stack>
      );
    }

    return null;
  };

  const getMarkup = (): JSX.Element => {
    if (loading) {
      return <Loader loading={loading} />;
    }

    return (
      <>
        {headerContent()}
        <PieChart
          series={series}
          dataKeys={declineReasonsDataKeys}
          showCustomLabels
          options={{
            plotOptions: {
              pie: {
                offsetX: -40,
                offsetY: 5,
              },
            },
          }}
          width={300}
          zeroState={{
            label: t('hiring-dashboard-metrics:metrics.noDeclineReason'),
            height: 240,
          }}
        />
      </>
    );
  };

  return (
    <SectionWrap
      header={t('hiring-dashboard-metrics:metrics.declineReasons')}
      subHeader={t('hiring-dashboard-metrics:metrics.declineReasonsSubHeader')}
      onDownload={series.length ? onDownload : undefined}
    >
      {getMarkup()}
    </SectionWrap>
  );
};

export default DeclineReasonsChart;
